import * as React from 'react';
import AsyncSelect from 'react-select/async';
import Select from 'react-select';
import { IDropdownData, IDropdownLabel, IMeta } from 'models/common';
import { AvatarSmall } from 'components';
import { IconName } from 'helpers/renderIcon';
import { deleteURLSearchParams } from 'helpers/common';
const classNames = require('classnames');

const SELECTMENU_USAGE = {
	portal: '',
	admin: 'portalAdminSelectMenu',
};

export type IBaseProps<
	TValue = string,
	TKey extends string | number = string,
	TItem extends IDropdownData<TValue, TKey> = IDropdownData<TValue, TKey>,
> = {
	className?: string;
	input?: any;
	autoFocus?: boolean;
	meta?: IMeta;
	customDropdownIndicator?: JSX.Element;
	customControlElement?: React.ComponentType | (() => JSX.Element);
	menuPlacement?: string;
	usage?: 'portal' | 'admin';
	multi?: boolean;
	closeOnSelect?: boolean;
	label?: string;
	clearable?: boolean;
	isRequired?: boolean;
	isDisabled?: boolean;
	searchable?: boolean;
	placeholder?: string;
	onChangeAction?: (ev: any) => void;
	noLabel?: any;
	styles?: any;
	description?: string;
	information?: string;
	formatOptionLabel?: (item: TItem) => JSX.Element;
	withImages?: boolean;
	clearAfterSelect?: boolean;
	customDropdownIcon?: IconName;
	clearValueOption?: boolean;
	highlightWhenValueSet?: boolean;
	putSelectedValueInUrlQuery?: string; // url prop key name
	optionsHaveCheckbox?: boolean;
	displayOnlyMenuValues?: boolean;
	location?: any; // obtained with withRouter
	twoPartLabels?: boolean;
	emptyValueLabel?: string;
	dontCaptureMenuScroll?: boolean;
};

export interface IStaticProps<
	TValue = string,
	TKey extends string | number = string,
	TItem extends IDropdownData<TValue, TKey> = IDropdownData<TValue, TKey>,
> extends IBaseProps<TValue, TKey, TItem> {
	items?: TItem[];
}

interface IAsyncProps<
	TValue = string,
	TKey extends string | number = string,
	TItem extends IDropdownData<TValue, TKey> = IDropdownData<TValue, TKey>,
> extends IBaseProps<TValue, TKey, TItem> {
	asyncLoad: (search: string) => Promise<TItem[]>;
}

interface IState {
	searchValue?: string;
	menuOpen?: boolean;
	urlParamsValue: string;
}

export type IProps<
	TValue = string,
	TKey extends string | number = string,
	TItem extends IDropdownData<TValue, TKey> = IDropdownData<TValue, TKey>,
> = IStaticProps<TValue, TKey, TItem> | IAsyncProps<TValue, TKey, TItem>;

class BookingSelectMenuW<
	TValue = string,
	TKey extends string | number = string,
	TItem extends IDropdownData<TValue, TKey> = IDropdownData<TValue, TKey>,
> extends React.Component<IProps<TValue, TKey, TItem>, IState> {
	constructor(props) {
		super(props);
		this.handleChange = this.handleChange.bind(this);
		this.onInputChange = this.onInputChange.bind(this);
		this.handleMenuOpen = this.handleMenuOpen.bind(this);
		this.handleMenuClose = this.handleMenuClose.bind(this);
		this.handleClearSelectedValue = this.handleClearSelectedValue.bind(this);
		this.state = {
			searchValue: '',
			menuOpen: false,
			urlParamsValue: '',
		};
	}
	internalItems = (this.props as any)?.items?.map((item) => {
		return {
			value: item.key,
			label: item.value,
			subLabel: item.subLabel,
			isDisabled: item.isDisabled !== undefined && item.isDisabled !== null ? item.isDisabled : false,
			image: item.image ? item.image : undefined,
		};
	});
	selectRef = React.createRef();

	public componentDidUpdate(prevProps: IProps<TValue, TKey>) {
		if (prevProps.input.value !== this.props.input.value) {
			this.forceUpdate();
			if (prevProps.input.value && !this.props.input.value) {
				//when clearing from outside
				this.setState({ searchValue: '' });
			}
		}
	}

	public static defaultProps: Partial<IProps> = {
		clearable: false,
		closeOnSelect: true,
		searchable: true,
		placeholder: '',
		isDisabled: false,
		multi: false,
	};

	private handleChange(values) {
		let value = null;
		if (this.props.multi) {
			value = values ? values.map((v) => v.value) : null;
		} else {
			value = values ? values.value : '';
		}
		if (this.props.onChangeAction) {
			this.props.onChangeAction(value);
		}
		if (this.props.input.onChange) {
			this.props.input.onChange(value);
		}
		if (this.props.clearAfterSelect && this.selectRef.current) {
			(this.selectRef as any).current.select.clearValue();
		}
	}

	private transformValue(value, options, multi) {
		if (multi && typeof value === 'string') {
			return [];
		}

		const filteredOptions = options.filter((option) => {
			return multi ? value.indexOf(option.value) !== -1 : option.value === value;
		});

		return multi ? filteredOptions : filteredOptions[0];
	}

	private onInputChange(searchValue: string) {
		this.setState({ searchValue });
	}

	private handleMenuOpen() {
		this.setState({ menuOpen: true });
	}
	private handleMenuClose() {
		this.setState({ menuOpen: false });
	}
	private handleClearSelectedValue(e: React.MouseEvent) {
		if (e) {
			e.stopPropagation();
			e.preventDefault();
		}
		if ((this.selectRef as any).current) {
			(this.selectRef as any).current.select.clearValue();
			this.props.input.onChange(undefined);
		}
		if (this.state.urlParamsValue) {
			this.setState({ urlParamsValue: '' });
		}
		deleteURLSearchParams([this.props.putSelectedValueInUrlQuery]);
	}

	public render() {
		const {
			information,
			meta,
			multi,
			closeOnSelect,
			clearable,
			searchable,
			isRequired,
			isDisabled,
			usage,
			formatOptionLabel,
			className,
			customControlElement,
			withImages,
			highlightWhenValueSet,
			optionsHaveCheckbox,
			displayOnlyMenuValues,
			twoPartLabels,
			emptyValueLabel,
			dontCaptureMenuScroll,
		} = this.props;
		const { value, name, onFocus } = this.props.input;

		let description = this.props.description;
		let status = '';
		if (meta?.touched) {
			if (meta?.warning) {
				status = 'withWarning';
				description = meta.warning;
			}
			if (meta?.error) {
				description = meta.error;
				status = 'withError';
			}
		}

		const selectProps: { [key: string]: any } = {
			...this.props,
			components: {
				IndicatorSeparator: null,
			},
			className: classNames('inputField', {
				value: (Boolean(value) || value === 0) && !(Array.isArray(value) && !value.length),
			}),
			menuPlacement: this.props.menuPlacement ? this.props.menuPlacement : 'bottom',
			classNamePrefix: 'react-select',
			name: name + '_dropdown',
			onChange: (value) => this.handleChange(value),
			onFocus,
			isMulti: multi,
			placeholder: this.props.placeholder ? this.props.placeholder : ' ',
			closeMenuOnSelect: closeOnSelect,
			isClearable: clearable,
			isSearchable: searchable,
			isDisabled: isDisabled,
			formatOptionLabel,
			inputValue: this.state.searchValue,
			onInputChange: this.onInputChange,
		};
		if (customControlElement) {
			selectProps.components.Control = customControlElement;
		}
		if (withImages && !formatOptionLabel) {
			selectProps.formatOptionLabel = (item: IDropdownLabel) => (
				<div className="dropdownItem">
					<AvatarSmall imageUrl={item.image} name={item.label} />
					<span>{item.label}</span>
				</div>
			);
		}

		if (twoPartLabels && !formatOptionLabel) {
			selectProps.formatOptionLabel = (item: IDropdownLabel) => {
				return (
					<div className="dropdownItem">
						<span className="labelMain">{item.label}</span>
						<span className="labelSub">{item.subLabel}</span>
					</div>
				);
			};
		}

		let SelectComponent = Select;

		if ('asyncLoad' in this.props) {
			SelectComponent = AsyncSelect;
			selectProps.loadOptions = this.props.asyncLoad;
			selectProps.defaultOptions = [];
			selectProps.cacheOptions = false;
		} else {
			this.internalItems = (this.props as any)?.items?.map((item) => {
				return {
					value: item.key,
					label: item.value,
					subLabel: item.subLabel,
					isDisabled: item.isDisabled !== undefined && item.isDisabled !== null ? item.isDisabled : false,
					image: item.image ? item.image : undefined,
				};
			});
			let transformedValue = null;
			if (this.internalItems && this.internalItems.length > 0) {
				transformedValue = this.transformValue(value, this.internalItems, multi);
				if (typeof transformedValue === 'undefined') {
					transformedValue = null;
				}
			}

			// add default value if not required
			if (!isRequired && !multi) {
				if (!this.internalItems.filter((x) => x.value === '')[0]) {
					this.internalItems.unshift({
						value: '' as any,
						label: (<>{emptyValueLabel ? emptyValueLabel : <>&nbsp;</>}</>) as any,
						isDisabled: false,
						image: '',
					});
				}
			}

			selectProps.options = this.internalItems;
			selectProps.value = this.state.urlParamsValue || transformedValue || '';
		}
		selectProps.noOptionsMessage = () => null;
		selectProps.onMenuOpen = this.handleMenuOpen;
		selectProps.onMenuClose = this.handleMenuClose;
		selectProps.menuIsOpen = displayOnlyMenuValues ? true : undefined;
		selectProps.captureMenuScroll = dontCaptureMenuScroll ? false : undefined;

		let informationContainer: JSX.Element = null;
		if (typeof information !== 'undefined' && information !== null) {
			informationContainer = <div className="informationBlock">{information}</div>;
		}
		const usageT = usage ? SELECTMENU_USAGE[usage] : SELECTMENU_USAGE.portal;

		return (
			<label
				className={classNames(
					className,
					'bookingFieldGroup BookingSelectMenu',
					usageT,
					status,
					{ value },
					displayOnlyMenuValues ? 'displayOnlyMenuValues' : '',
				)}
			>
				{informationContainer}
				<div
					className={`inputHolder ${
						highlightWhenValueSet &&
						(((this.selectRef as any).current && (this.selectRef as any).current.state.value) ||
							this.state.urlParamsValue)
							? 'highlight'
							: ''
					} ${isDisabled ? 'disabled' : ''}`}
				>
					<SelectComponent
						{...selectProps}
						ref={this.selectRef as any}
						className={`${selectProps.className} ${optionsHaveCheckbox ? 'optionsHaveCheckboxes' : ''}`}
						styles={{
							dropdownIndicator: (base, state) => ({
								...base,
								transition: 'all .2s ease!important',
								transform: state.selectProps.menuIsOpen ? 'rotate(180deg)!important' : null,
							}),
						}}
					/>
				</div>
				{description ? <div className="note">{description}</div> : null}
			</label>
		);
	}
}

export const BookingSelectMenu = BookingSelectMenuW;
